import { useCallback, useRef, useState } from "react"

import { CHAT_STORAGE_KEY, INITIAL_GREETING, MAX_API_MESSAGES, MAX_MESSAGE_LENGTH } from "../constants/chat"

import { streamChatCompletion } from "../utils/openai"
import { extractTripDataFromMessage, mapToFormData, validateTripData } from "../utils/tripDataExtractor"

const loadMessages = () => {
   try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY)
      if (stored) return JSON.parse(stored)
   } catch {
      // Ignore parse errors
   }
   return [{ role: "assistant", content: INITIAL_GREETING }]
}

const saveMessages = (messages) => {
   try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages))
   } catch {
      // Ignore storage errors
   }
}

const useChatStream = () => {
   const [messages, setMessages] = useState(loadMessages)
   const [isStreaming, setIsStreaming] = useState(false)
   const [error, setError] = useState(null)
   const [tripData, setTripData] = useState(null)

   const abortRef = useRef(null)
   const accumulatedRef = useRef("")
   const throttleRef = useRef(null)

   const updateLastAssistantMessage = useCallback((content) => {
      setMessages((prev) => {
         const updated = [...prev]
         const lastIndex = updated.length - 1
         if (updated[lastIndex]?.role === "assistant") {
            updated[lastIndex] = { ...updated[lastIndex], content }
         }
         return updated
      })
   }, [])

   const sendMessage = useCallback(
      async (userMessage) => {
         if (!userMessage.trim() || isStreaming) return

         const userMsg = { role: "user", content: userMessage.trim() }
         const assistantMsg = { role: "assistant", content: "" }

         setMessages((prev) => {
            const updated = [...prev, userMsg, assistantMsg]
            saveMessages([...prev, userMsg])
            return updated
         })

         setIsStreaming(true)
         setError(null)
         accumulatedRef.current = ""

         const abortController = new AbortController()
         abortRef.current = abortController

         const recentMessages = messages.slice(-MAX_API_MESSAGES)
         const apiMessages = [
            ...recentMessages
               .filter((m) => m.role === "user" || m.role === "assistant")
               .map((m) => ({ role: m.role, content: String(m.content).slice(0, MAX_MESSAGE_LENGTH) })),
            userMsg,
         ]

         await streamChatCompletion({
            messages: apiMessages,
            signal: abortController.signal,
            onChunk: (delta, accumulated) => {
               accumulatedRef.current = accumulated
               if (!throttleRef.current) {
                  throttleRef.current = setTimeout(() => {
                     updateLastAssistantMessage(accumulatedRef.current)
                     throttleRef.current = null
                  }, 50)
               }
            },
            onDone: (fullContent) => {
               if (throttleRef.current) {
                  clearTimeout(throttleRef.current)
                  throttleRef.current = null
               }
               updateLastAssistantMessage(fullContent)
               setIsStreaming(false)
               abortRef.current = null

               setMessages((prev) => {
                  saveMessages(prev)
                  return prev
               })

               const extracted = extractTripDataFromMessage(fullContent)
               if (extracted) {
                  const validation = validateTripData(extracted)
                  if (validation.valid) {
                     setTripData(mapToFormData(extracted))
                  }
               }
            },
            onError: (err) => {
               setError(err.message || "Something went wrong")
               setIsStreaming(false)
               abortRef.current = null

               setMessages((prev) => {
                  const updated = prev.filter(
                     (m, i) => !(i === prev.length - 1 && m.role === "assistant" && !m.content),
                  )
                  saveMessages(updated)
                  return updated
               })
            },
         })
      },
      [isStreaming, messages, updateLastAssistantMessage],
   )

   const stopStreaming = useCallback(() => {
      if (abortRef.current) {
         abortRef.current.abort()
         abortRef.current = null
      }
      if (throttleRef.current) {
         clearTimeout(throttleRef.current)
         throttleRef.current = null
      }
      if (accumulatedRef.current) {
         updateLastAssistantMessage(accumulatedRef.current)
      }
      setIsStreaming(false)
      setMessages((prev) => {
         saveMessages(prev)
         return prev
      })
   }, [updateLastAssistantMessage])

   const clearMessages = useCallback(() => {
      const initial = [{ role: "assistant", content: INITIAL_GREETING }]
      setMessages(initial)
      setTripData(null)
      setError(null)
      saveMessages(initial)
   }, [])

   const clearTripData = useCallback(() => {
      setTripData(null)
   }, [])

   return {
      messages,
      isStreaming,
      error,
      tripData,
      sendMessage,
      stopStreaming,
      clearMessages,
      clearTripData,
   }
}

export default useChatStream
